// src/pages/TechnicianReportPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Paper, Typography, Button, CircularProgress, Divider, 
  Card, CardContent, Stack, Chip, Grid
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

const TechnicianReportPage = ({ user }) => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [todaysReport, setTodaysReport] = useState(null);
    
    const todayKey = format(new Date(), 'yyyy-MM-dd');

    useEffect(() => {
        const checkExistingReport = async () => {
            const activeReport = await getData('active_technician_report');
            if (activeReport && activeReport.date === todayKey) {
                setTodaysReport(activeReport);
            }
            setLoading(false);
        };
        checkExistingReport();
    }, [todayKey]);

    const handleStartReport = async () => {
        const newReport = {
            id: `tech_${Date.now()}`,
            date: todayKey,
            createdBy: user.username,
            status: 'open',
            data: {
                calderas: {},
                introductor: {},
                hibrida: {},
                kannegiesser: [{
                    id: 1, p1: '', p2: '', p3: '', p4: '',
                    trabajador1: '', trabajador2: '', trabajador3: ''
                }]
            }
        };
        await saveData('active_technician_report', newReport);
        navigate('/technician-form');
    };
    
    const handleContinueReport = () => {
        navigate('/technician-form');
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress size={60} />
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    {/* Header Section */}
                    <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <AssignmentIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                        <Typography variant="h3" fontWeight="bold" gutterBottom>
                            {t('tech_report_title')}
                        </Typography>
                        <Chip 
                            label={format(new Date(), 'PPPP', { locale: es })}
                            color="primary"
                            variant="filled"
                            sx={{ fontSize: '1.1rem', py: 2, px: 3 }}
                        />
                    </Stack>

                    <Divider sx={{ mb: 4 }} />

                    {/* Report Status Section */}
                    {todaysReport ? (
                        <Stack spacing={4} alignItems="center">
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="success.main" gutterBottom>
                                    {t('tech_report_existing_report')}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {t('tech_report_continue_today')}
                                </Typography>
                            </Box>
                            
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<EditNoteIcon />}
                                    onClick={handleContinueReport}
                                    sx={{ 
                                        py: 1.5, 
                                        px: 4, 
                                        fontSize: '1.1rem',
                                        borderRadius: 2
                                    }}
                                >
                                    {t('tech_report_continue_today')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => navigate('/technician-form')}
                                    sx={{ 
                                        py: 1.5, 
                                        px: 4, 
                                        fontSize: '1.1rem',
                                        borderRadius: 2
                                    }}
                                >
                                    {t('actions_view')}
                                </Button>
                            </Stack>

                            {/* Report Info Card */}
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    mt: 2, 
                                    p: 2, 
                                    borderRadius: 2,
                                    backgroundColor: 'success.light',
                                    borderColor: 'success.main'
                                }}
                            >
                                <Typography variant="body2" color="success.dark">
                                    <strong>{t('common_status')}:</strong> {t('status_active')} • <strong>{t('common_technician')}:</strong> {user.username}
                                </Typography>
                            </Card>
                        </Stack>
                    ) : (
                        <Stack spacing={4} alignItems="center">
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="text.primary" gutterBottom>
                                    {t('tech_report_no_report')}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {t('tech_report_start_today')}
                                </Typography>
                            </Box>
                            
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                startIcon={<PlayCircleOutlineIcon />}
                                onClick={handleStartReport}
                                sx={{ 
                                    py: 2, 
                                    px: 6, 
                                    fontSize: '1.2rem',
                                    borderRadius: 2,
                                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                }}
                            >
                                {t('tech_report_start_today')}
                            </Button>

                            {/* Features List */}
                            <Stack spacing={1} sx={{ mt: 2, textAlign: 'left' }}>
                                <Typography variant="body2" color="text.secondary">
                                    • {t('equipment_col_name')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {t('nav_maintenance')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {t('tech_form_tab_introductor')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {t('tech_form_tab_kannegiesser')}
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {/* Quick Actions */}
                    <Divider sx={{ my: 4 }} />
                    
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <Button
                            variant="text"
                            onClick={() => navigate('/archive/technician')}
                            sx={{ borderRadius: 2 }}
                        >
                            {t('menu_archiveTechnician')}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => navigate('/maintenance')}
                            sx={{ borderRadius: 2 }}
                        >
                            {t('nav_maintenance')}
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => navigate('/equipment')}
                            sx={{ borderRadius: 2 }}
                        >
                            {t('nav_equipment')}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Statistics Cards */}
            {todaysReport && (
                <Grid container spacing={2} sx={{ mt: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" color="primary.main">
                                5
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('tabs_tunnel_production')}
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" color="success.main">
                                12
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('nav_equipment')}
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" color="info.main">
                                3
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('common_notes')}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default TechnicianReportPage;